// The folder rail. Children load when a branch is first opened rather than up front, because the tree is
// as deep as the org files things and a fund folder alone holds hundreds of branches.
//
// Expansion is remembered in localStorage, the way the main nav remembers its collapsed sections: the
// folder someone works out of should still be open tomorrow morning.

import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Icon } from '/imports/ui/Icon';
import { toastOnError } from '/imports/ui/utils';
import { DriveFolderRow, driveFolderChildren } from '/imports/api/drive';

const STORE_KEY = 'drive.expandedFolders';

const DriveFolderTreeStyled = styled.div`
  .tree-row { display: flex; align-items: center; gap: 0.1rem; }
  .tree-row > .caret {
    background: none; border: none; cursor: pointer; padding: 0.1rem; line-height: 0; width: 1.1rem;
  }
  .tree-row > .caret.empty { visibility: hidden; }
  .tree-row > .node {
    display: flex; align-items: center; gap: 0.35rem; flex: 1; min-width: 0;
    background: none; border: none; cursor: pointer; text-align: left;
    padding: 0.25rem 0.35rem; border-radius: ${css.inputBorderRadius}; font-size: 0.85rem; color: ${css.grey1};
  }
  .tree-row > .node:hover { background: ${css.grey9}; }
  .tree-row > .node.selected { background: ${css.primaryColorTransparent}; color: ${css.primaryColor}; font-weight: 600; }
  .tree-row > .node .label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .tree-row > .node .count { color: ${css.grey4}; font-size: 0.72rem; font-variant-numeric: tabular-nums; }
  .tree-children { margin-left: 0.85rem; border-left: 1px solid ${css.grey7}; padding-left: 0.15rem; }
  .tree-empty { color: ${css.grey4}; font-size: 0.8rem; padding: 0.3rem 0.4rem; }
`;

/** Reads the remembered open branches. A browser that refuses storage simply starts collapsed. */
const readExpanded = (): string[] => {
  try {
    const held = window.localStorage.getItem(STORE_KEY);
    return held ? JSON.parse(held) : [];
  }
  catch {
    return [];
  }
};

const writeExpanded = (ids: string[]) => {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(ids.slice(0, 200)));
  }
  catch {/* a private window, or storage turned off: the tree just forgets */}
};

interface BranchProps {
  folder: DriveFolderRow;
  selectedId?: string;
  expanded: string[];
  childrenById: { [id: string]: DriveFolderRow[] | undefined };
  onToggle(id: string): void;
  onSelect(id: string): void;
}

const Branch = (props: BranchProps) => {
  const { folder, childrenById, expanded } = props;
  const open = expanded.includes(folder._id);
  const children = childrenById[folder._id];
  return (
    <div className="tree-branch">
      <div className="tree-row">
        <button
          type="button"
          className={`caret ${folder.childFolders ? '' : 'empty'}`}
          onClick={() => props.onToggle(folder._id)}
          aria-label={folder.name}
        >
          <Icon icon={open ? 'expandMore' : 'chevronRight'} size={16} fill={css.grey3} />
        </button>
        <button
          type="button"
          className={`node ${props.selectedId === folder._id ? 'selected' : ''}`}
          onClick={() => props.onSelect(folder._id)}
        >
          <Icon icon="folder" size={16} fill={css.primaryColor} />
          <span className="label">{folder.name}</span>
          {!folder.inherited && !!folder.parentId && <Icon icon="lock" size={12} fill={css.grey4} />}
          {!!folder.files && <span className="count">{folder.files}</span>}
        </button>
      </div>
      {open && (
        <div className="tree-children">
          {!children && <div className="tree-empty">{strings.Loading()}</div>}
          {children?.map((child) => (
            <Branch key={child._id} {...props} folder={child} />
          ))}
        </div>
      )}
    </div>
  );
};

export interface DriveFolderTreeProps {
  selectedId?: string;
  // Bumped by the page after anything that changes the shape of the tree, so a new or moved folder shows
  // up without a reload.
  reload?: number;
  onSelect(folderId: string): void;
}

export const DriveFolderTree = (props: DriveFolderTreeProps) => {
  const [roots, setRoots] = useState<DriveFolderRow[]>();
  const [childrenById, setChildrenById] = useState<{ [id: string]: DriveFolderRow[] | undefined }>({});
  const [expanded, setExpanded] = useState<string[]>(readExpanded);

  const load = useCallback(toastOnError(async (parentId?: string) => {
    const rows = await driveFolderChildren.call({ parentId });
    if (parentId) setChildrenById((held) => ({ ...held, [parentId]: rows }));
    else setRoots(rows);
  }), []);

  useEffect(() => {
    load();
    // Anything already open reloads too, so a move shows in both the branch it left and the one it joined.
    for (const id of readExpanded()) load(id);
  }, [props.reload]);

  const toggle = (id: string) => {
    const next = expanded.includes(id) ? expanded.filter((held) => held !== id) : [...expanded, id];
    setExpanded(next);
    writeExpanded(next);
    if (!childrenById[id]) load(id);
  };

  return (
    <DriveFolderTreeStyled>
      {roots?.map((root) => (
        <Branch
          key={root._id}
          folder={root}
          selectedId={props.selectedId}
          expanded={expanded}
          childrenById={childrenById}
          onToggle={toggle}
          onSelect={props.onSelect}
        />
      ))}
      {roots && !roots.length && <div className="tree-empty">{strings.NoFolders()}</div>}
    </DriveFolderTreeStyled>
  );
};
